/**
 * Helper hook for FK pickers. Wraps a paginated `listXxx` API in a
 * UzCombobox-friendly shape: server-side search via the list endpoint's
 * `search` param, plus a separate "fetch by id" pass so the currently-
 * selected row's label still shows when it's not in the current search
 * results.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { UzComboboxOption } from '@utazzo/ui';

export interface PickerSource<TItem> {
  /** Stable react-query cache key prefix. */
  cacheKey: string;
  /** Fetch a paginated list filtered by `search`. */
  fetch: (search: string) => Promise<TItem[]>;
  /** Resolve a single row by id (used so selected labels survive page changes). */
  fetchOne: (id: string) => Promise<TItem | null>;
  /** Map a row -> combobox option. */
  toOption: (row: TItem) => UzComboboxOption;
}

export const usePickerOptions = <TItem>(
  src: PickerSource<TItem>,
  selectedId: string | null,
): {
  options: UzComboboxOption[];
  loading: boolean;
  search: string;
  setSearch: (q: string) => void;
} => {
  const [search, setSearch] = useState('');
  const listQuery = useQuery({
    queryKey: [src.cacheKey, 'list', search],
    queryFn: () => src.fetch(search),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  const selectedQuery = useQuery({
    queryKey: [src.cacheKey, 'one', selectedId ?? ''],
    queryFn: () => src.fetchOne(selectedId as string),
    enabled: Boolean(selectedId),
    staleTime: 5 * 60_000,
  });

  const options = useMemo(() => {
    const rows = listQuery.data ?? [];
    const mapped = rows.map(src.toOption);
    // If the selected row isn't in the current list (e.g. operator changed
    // search query after selecting), inject it at the top so the label
    // doesn't fall back to the raw uuid.
    if (selectedId && selectedQuery.data) {
      const already = mapped.some((m) => m.value === selectedId);
      if (!already) mapped.unshift(src.toOption(selectedQuery.data));
    }
    return mapped;
  }, [listQuery.data, selectedQuery.data, selectedId, src]);

  return {
    options,
    loading: listQuery.isFetching || selectedQuery.isFetching,
    search,
    setSearch,
  };
};
